import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCertificates } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { FileBadge2, Plus, Cpu, ArrowRight, ShieldCheck, AlertTriangle, Brain, Settings2, LayoutPanelTop, BookOpen, FileSpreadsheet, ClipboardCheck } from 'lucide-react';
import { CertAiAccuracyWidget } from '../../components/CertAiAccuracyWidget';

const CertList = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  useEffect(() => { getCertificates().then(setCerts).finally(() => setLoading(false)); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Electrical Certificates"
        description="AI-assisted EICR / Minor Works / DEIC certificates. All values must be verified by a qualified electrician before issue."
        actions={
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:justify-end w-full sm:w-auto">
            <Link to={`${base}/certificates/new`} className="col-span-2 sm:order-3" data-testid="new-cert-link">
              <Button className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-base font-semibold py-5 sm:py-2.5 px-6" data-testid="new-cert-btn">
                <Plus className="h-5 w-5 mr-2" /> New Certificate
              </Button>
            </Link>
            <Link to={`${base}/certificates/rules`} className="sm:order-1">
              <Button variant="outline" className="w-full sm:w-36 justify-start sm:justify-center" data-testid="rules-btn">
                <Settings2 className="h-4 w-4 mr-2" /> Rules
              </Button>
            </Link>
            <Link to={`${base}/board-layouts`} className="sm:order-2">
              <Button variant="outline" className="w-full sm:w-36 justify-start sm:justify-center" data-testid="board-layouts-btn">
                <LayoutPanelTop className="h-4 w-4 mr-2" /> Board Layouts
              </Button>
            </Link>
            <Link to={`${base}/certificates/train`} className="sm:order-4">
              <Button variant="outline" className="w-full sm:w-36 justify-start sm:justify-center" data-testid="train-ai-btn">
                <Brain className="h-4 w-4 mr-2" /> Train AI
              </Button>
            </Link>
            <Link to={`${base}/certificates/reference-library`} className="sm:order-5">
              <Button variant="outline" className="w-full sm:w-36 justify-start sm:justify-center" data-testid="reference-library-btn">
                <BookOpen className="h-4 w-4 mr-2" /> Reference Library
              </Button>
            </Link>
            <Link to={`${base}/certificates/scan-previous`} className="col-span-2 sm:order-6 sm:col-span-1 sm:w-auto">
              <Button variant="outline" className="w-full sm:w-44 justify-start sm:justify-center text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="scan-previous-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Scan previous cert
              </Button>
            </Link>
            <Link to={`${base}/eicr-reports`} className="col-span-2 sm:order-7 sm:col-span-1 sm:w-auto">
              <Button variant="outline" className="w-full sm:w-44 justify-start sm:justify-center text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="eicr-reports-btn">
                <ClipboardCheck className="h-4 w-4 mr-2" /> Observation Reports
              </Button>
            </Link>
          </div>
        }
      />

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start gap-3">
        <Cpu className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-800">
          <div className="font-semibold">AI-assisted extraction</div>
          <p>Upload photos of distribution boards, MCBs/RCDs, test meter readings, or previous certificates. Claude Sonnet 4.5 vision pre-fills the form. <span className="font-semibold">A qualified engineer must verify and sign before the certificate is issued.</span></p>
        </div>
      </div>

      <CertAiAccuracyWidget months={1} />

      {loading ? (
        <div className="p-8 text-slate-500">Loading...</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium text-slate-600">ID</th>
                <th className="px-4 py-3 font-medium text-slate-600">Type</th>
                <th className="px-4 py-3 font-medium text-slate-600">Site</th>
                <th className="px-4 py-3 font-medium text-slate-600">Inspector</th>
                <th className="px-4 py-3 font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 font-medium text-slate-600">Condition</th>
                <th className="px-4 py-3 font-medium text-slate-600">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {certs.map(c => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{c.id}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.certType}</td>
                  <td className="px-4 py-3 text-slate-600">{c.siteAddress || c.siteId}</td>
                  <td className="px-4 py-3 text-slate-700">{c.inspectorName || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{c.dateOfInspection || '—'}</td>
                  <td className="px-4 py-3">
                    {c.overallCondition?.toLowerCase().startsWith('satisf')
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded"><ShieldCheck className="h-3 w-3" /> {c.overallCondition}</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded"><AlertTriangle className="h-3 w-3" /> {c.overallCondition || '—'}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.issued
                      ? <span className="inline-flex text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">Issued</span>
                      : <span className="inline-flex text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded">Draft</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`${base}/certificates/${c.id}`} className="text-sm text-blue-700 hover:text-blue-800 inline-flex items-center gap-1">Open <ArrowRight className="h-3 w-3" /></Link>
                  </td>
                </tr>
              ))}
              {certs.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-slate-500">
                    <FileBadge2 className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    No certificates yet. Click <span className="font-medium">New Certificate</span> to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CertList;
